import { fg } from '@atlaskit/platform-feature-flags';

import type { Edition, Product } from '../types';

import { getChargeElementKey } from './get-charge-element-key';
import { getJiraApplicationKey } from './get-jira-application-key';
import { getHamsKey } from './product-to-hams-key';
import { dialogIdToKey } from './use-mark-dialog-as-seen';

/**
 * Returns the variables required for the root Edition Awareness query.
 */
export function getQueryConfig(
	product: Product,
	edition: Edition,
	cloudId: string,
	accountId?: string | null,
): {
	// AccountID is a required value, but the condition will ensure it is not used if undefined/null
	accountId: string;
	applicationKey: 'JIRA_SOFTWARE' | 'JIRA_PRODUCT_DISCOVERY' | 'JIRA_SERVICE_DESK';
	chargeElement: string;
	cloudId: string; // The entitlement filter ensures that we apply the `inTrialOrPreDunning` only when an entitlement is
	// on a non free edition. The filter is based on the trial end timestamp inside of TCS, this means that
	// if a customer downgrades their product to free while on a trial, then they will only show up
	// if inTrialOrPreDunning is set to true as the filter assumes they are still on trial.
	entitlementFilter: { inTrialOrPreDunning: boolean } | null;
	onboardingStateKeys: string[];
	productKey: string;
	shouldCheckBillingAdmin: boolean;
	shouldCheckForChargeQuantities: boolean;
	shouldCheckForDirectBuyChangeOffering: boolean;
	shouldCheckForPaymentDetails: boolean;
	shouldCheckForStorageLimit: boolean;
	shouldCheckForStorageUsageConfluence: boolean;
	shouldCheckForStorageUsageJira: boolean;
	shouldCheckForTrial: boolean;
	shouldCheckForUserEmail: boolean;
	shouldCheckUserPreferences: boolean;
	shouldIncludeProfile: boolean;
	shouldSkip: boolean;
} {
	const isStandardTrialsUserAndStorageLimitBreakersEnabledJira =
		product === 'jira' &&
		edition === 'standard' &&
		fg('jira_edition_awareness_standard_trials_gate');
	const isStandardTrialsUserAndStorageLimitBreakersEnabledConnie =
		product === 'confluence' &&
		edition === 'standard' &&
		fg('connie_edition_awareness_standard_trials_gate');
	const isStorageHardLimitEnabled =
		product === 'jira' && edition === 'free' && !!fg('jira-storage-hard-limit-gate'); // Workaround to ensure fg is not undefined to avoid errors in unit tests - https://hello.atlassian.net/wiki/x/Uy4HSgE
	const isSpecialOfferSixtyEnabled =
		product === 'jira' &&
		edition === 'free' &&
		!!fg('special_offer_60_days_jira_standard_kill_switch');
	const isStandardTrialsUserAndStorageLimitBreakersEnabled =
		isStandardTrialsUserAndStorageLimitBreakersEnabledJira ||
		isStandardTrialsUserAndStorageLimitBreakersEnabledConnie;
	const isBillingAdminEnabled = product === 'jira' || product === 'confluence';
	const shouldCheckForPaymentDetails =
		(edition === 'free' &&
			(product === 'jira-service-management' || product === 'jira-product-discovery') &&
			fg('jsm_jpd_free_to_standard_ea_update_gate')) ||
		(edition === 'free' &&
			product === 'confluence' &&
			(fg('connie_end_user_request_ea_vc90_check') || fg('connie_end_user_request_kill_switch')));

	const shouldIncludeProfile = product === 'confluence' && fg('ea_gups_confluence');
	const shouldCheckUserPreferences = product === 'confluence'

	const onboardingStateKeys: string[] =
		product === 'confluence'
			? [
					dialogIdToKey('feature-onboarding-modal', product, edition),
					'end-user-request-to-upgrade-latest-date',
				]
			: [dialogIdToKey('feature-onboarding-modal', product, edition)];

	return {
		shouldSkip:
			product === 'jira-product-discovery' && fg('jpd_disable_edition_awareness') === true,
		shouldCheckForDirectBuyChangeOffering: isStorageHardLimitEnabled || isSpecialOfferSixtyEnabled,
		shouldCheckForTrial: edition === 'premium' || edition === 'standard',
		cloudId,
		productKey: getHamsKey(product),
		shouldCheckForChargeQuantities:
			edition === 'free' || isStandardTrialsUserAndStorageLimitBreakersEnabled,
		// The entitlement filter ensures that we apply the `inTrialOrPreDunning` only when an entitlement is
		// on a non free edition. The filter is based on the trial end timestamp inside of TCS, this means that
		// if a customer downgrades their product to free while on a trial, then they will only show up
		// if inTrialOrPreDunning is set to true as the filter assumes they are still on trial.
		entitlementFilter: edition !== 'free' ? { inTrialOrPreDunning: true } : null,
		chargeElement: getChargeElementKey(product),
		shouldCheckBillingAdmin: isBillingAdminEnabled,
		shouldCheckForStorageUsageJira:
			isStandardTrialsUserAndStorageLimitBreakersEnabledJira || isStorageHardLimitEnabled,
		applicationKey: getJiraApplicationKey(product) ?? 'JIRA_SOFTWARE',
		shouldCheckForStorageUsageConfluence: isStandardTrialsUserAndStorageLimitBreakersEnabledConnie,
		// AccountID is a required value, but the condition will ensure it is not used if undefined/null
		accountId: accountId ?? '',
		shouldCheckForUserEmail: !!accountId && product === 'jira' && edition === 'standard',
		shouldCheckForStorageLimit: isStorageHardLimitEnabled,
		shouldCheckForPaymentDetails,
		shouldIncludeProfile,
		shouldCheckUserPreferences,
		onboardingStateKeys,
	};
}
